package com.trevorwiebe.timesheet.mileage.data

import com.trevorwiebe.timesheet.core.data.FirestoreListenerRegistry
import com.trevorwiebe.timesheet.core.data.Utils.errorWrapper
import com.trevorwiebe.timesheet.core.domain.CoreRepository
import com.trevorwiebe.timesheet.core.domain.TSResult
import com.trevorwiebe.timesheet.core.domain.Util
import com.trevorwiebe.timesheet.mileage.data.dto.MileageEntryDto
import com.trevorwiebe.timesheet.mileage.data.dto.toMileageEntry
import com.trevorwiebe.timesheet.mileage.data.dto.toMileageEntryDto
import com.trevorwiebe.timesheet.mileage.domain.MileageRepository
import com.trevorwiebe.timesheet.mileage.domain.model.MileageEntry
import com.trevorwiebe.timesheet.mileage.domain.model.OdometerNeighbors
import com.trevorwiebe.timesheet.mileage.domain.model.TripType
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.firestore.Direction
import dev.gitlive.firebase.firestore.firestore
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.onStart
import kotlinx.datetime.LocalDate
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.atTime

class MileageRepositoryImpl(
    private val firebaseDatabase: Firebase,
    private val coreRepository: CoreRepository
) : MileageRepository {

    override suspend fun queryMileageEntries(
        startDate: LocalDate,
        endDate: LocalDate,
        employeeId: String?,
        equipmentId: String?,
        tripTypes: Set<TripType>,
    ): Flow<TSResult> {
        val orgIdResult = coreRepository.getOrganizationId()
        if (orgIdResult.error != null) return kotlinx.coroutines.flow.flow { emit(orgIdResult) }
        val orgId = orgIdResult.data as String

        // The stored "date" is an ISO instant string (UTC) produced from the local time zone, so
        // we build the inclusive day bounds the same way to keep the lexicographic range correct.
        val tz = TimeZone.currentSystemDefault()
        val startStr = with(Util) { startDate.atTime(0, 0, 0, 0).toIsoStringWithMillis(tz) }
        val endStr = with(Util) { endDate.atTime(23, 59, 59, 999_000_000).toIsoStringWithMillis(tz) }

        val query = firebaseDatabase.firestore
            .collection("organizations")
            .document(orgId)
            .collection("mileageEntries")
            .where {
                // all(...) AND-combines the constraints. Bare statements in a where{} block only
                // keep the LAST one, so optional filters must be assembled into a list and combined.
                // tripType is stored as the enum name, so the membership filter compares names.
                val filters = listOfNotNull(
                    employeeId?.let { "employeeId" equalTo it },
                    equipmentId?.let { "truckId" equalTo it },
                    tripTypes.takeIf { it.isNotEmpty() }?.let { "tripType" inArray it.map { t -> t.name } },
                    "date" greaterThanOrEqualTo startStr,
                    "date" lessThanOrEqualTo endStr,
                )
                all(*filters.toTypedArray())
            }
            .orderBy("date", Direction.DESCENDING)

        val flow = query.snapshots
            .onStart { emit(query.get()) }
            .map { snapshot ->
                snapshot.documents.map { doc -> doc.data<MileageEntryDto>().toMileageEntry(id = doc.id) }
            }
            .map { TSResult(data = it) }

        return FirestoreListenerRegistry.registerFlow(flow)
    }

    override suspend fun getLastMileageEntryForEquipment(equipmentId: String): TSResult {
        return errorWrapper {
            val orgId = coreRepository.getOrganizationId().data as String
            val snapshot = firebaseDatabase.firestore
                .collection("organizations")
                .document(orgId)
                .collection("mileageEntries")
                .where { "truckId" equalTo equipmentId }
                .orderBy("date", Direction.DESCENDING)
                .limit(1)
                .get()
            val entry = snapshot.documents.firstOrNull()
                ?.data<MileageEntryDto>()?.toMileageEntry(id = snapshot.documents.first().id)
            TSResult(data = entry)
        }
    }

    override suspend fun getOdometerNeighbors(
        equipmentId: String,
        date: LocalDateTime,
        mileageValue: Long,
        excludeEntryId: String?,
    ): TSResult {
        return errorWrapper {
            val orgId = coreRepository.getOrganizationId().data as String
            val dateStr = with(Util) { date.toIsoStringWithMillis(TimeZone.currentSystemDefault()) }
            val entries = firebaseDatabase.firestore
                .collection("organizations")
                .document(orgId)
                .collection("mileageEntries")

            suspend fun nearest(field: String, value: Any, below: Boolean): MileageEntry? {
                val direction = if (below) Direction.DESCENDING else Direction.ASCENDING
                var query = entries
                    .where {
                        all(
                            "truckId" equalTo equipmentId,
                            if (below) field lessThan value else field greaterThan value,
                        )
                    }
                    .orderBy(field, direction)
                if (field != "date") query = query.orderBy("date", direction)
                return query
                    .limit(2)
                    .get()
                    .documents
                    .map { it.data<MileageEntryDto>().toMileageEntry(id = it.id) }
                    .firstOrNull { it.id != excludeEntryId }
            }

            val miles = mileageValue.toDouble()
            TSResult(
                data = OdometerNeighbors(
                    previousByDate = nearest("date", dateStr, below = true),
                    nextByDate = nearest("date", dateStr, below = false),
                    lastBelowMileage = nearest("mileageValue", miles, below = true),
                    firstAboveMileage = nearest("mileageValue", miles, below = false),
                )
            )
        }
    }

    override suspend fun getLastServiceEntryForEquipment(equipmentId: String): TSResult {
        return errorWrapper {
            val orgId = coreRepository.getOrganizationId().data as String
            val snapshot = firebaseDatabase.firestore
                .collection("organizations")
                .document(orgId)
                .collection("mileageEntries")
                .where {
                    // all(...) AND-combines the constraints. Bare statements in a where{} block only
                    // keep the LAST one, so both equality filters must be passed to all().
                    all(
                        "truckId" equalTo equipmentId,
                        "tripType" equalTo "SERVICE",
                    )
                }
                .orderBy("date", Direction.DESCENDING)
                .limit(1)
                .get()
            val entry = snapshot.documents.firstOrNull()
                ?.data<MileageEntryDto>()?.toMileageEntry(id = snapshot.documents.first().id)
            TSResult(data = entry)
        }
    }

    override suspend fun addMileageEntry(entry: MileageEntry): TSResult {
        return errorWrapper {
            val orgId = coreRepository.getOrganizationId().data as String
            val result = firebaseDatabase.firestore
                .collection("organizations")
                .document(orgId)
                .collection("mileageEntries")
                .add(entry.toMileageEntryDto())
            TSResult(data = result.id)
        }
    }

    override suspend fun updateMileageEntry(entry: MileageEntry): TSResult {
        return errorWrapper {
            val orgId = coreRepository.getOrganizationId().data as String
            firebaseDatabase.firestore
                .collection("organizations")
                .document(orgId)
                .collection("mileageEntries")
                .document(entry.id)
                .set(entry.toMileageEntryDto(), merge = true)
            TSResult(data = "Mileage entry updated successfully")
        }
    }

    override suspend fun deleteMileageEntry(entryId: String): TSResult {
        return errorWrapper {
            val orgId = coreRepository.getOrganizationId().data as String
            firebaseDatabase.firestore
                .collection("organizations")
                .document(orgId)
                .collection("mileageEntries")
                .document(entryId)
                .delete()
            TSResult(data = "Mileage entry deleted successfully")
        }
    }

}
